import { useState, useEffect } from 'react';
import api from '../services/api';
import authService from '../services/authService';

interface OnboardingUrl {
    type: string;
    title: string;
    url: string;
    expirationDate: string;
}

interface PendingDocument {
    id: string;
    status: string;
    type: string;
    title: string;
    description: string;
    responsible?: {
        onboardingUrl: string;
        onboardingUrlExpirationDate: string;
    };
}

interface CompanyKycStatus {
    hasAsaasAccount: boolean;
    hasPendingDocuments: boolean;
    isKycComplete: boolean;
    pendingDocuments: PendingDocument[];
    onboardingUrls: OnboardingUrl[];
    rejectReasons?: string;
}

export function useCompanyKyc(companyId: string | null) {
    const [kycStatus, setKycStatus] = useState<CompanyKycStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchKycStatus = async () => {
        if (!companyId) {
            setKycStatus(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = authService.getToken();
            if (!token) {
                throw new Error('Token não encontrado');
            }

            const response = await api.get(`/api/kyc/company/${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data && response.data.success) {
                setKycStatus(response.data.data);
            } else {
                throw new Error(response.data.message || 'Erro ao buscar status KYC');
            }
        } catch (err: any) {
            console.error('Erro ao buscar status KYC:', err);
            setError(err.message || 'Erro ao buscar status KYC');
            // Não quebra o fluxo - define como KYC completo em caso de erro
            setKycStatus({
                hasAsaasAccount: false,
                hasPendingDocuments: false,
                isKycComplete: true,
                pendingDocuments: [],
                onboardingUrls: []
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKycStatus();
    }, [companyId]);

    return {
        kycStatus,
        loading,
        error,
        refetch: fetchKycStatus
    };
}
